import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { localized } from '@cue/translate';
import { remove } from 'remove-accents';
import { NavigationBasicInfo, NavigationNearestItem, NavigationResourceBasicInfo, TagData } from '@cue/data';

@Pipe({
  name: 'searchResult',
  standalone: true,
})
export class SearchResultPipe implements PipeTransform {
  translocoService = inject(TranslocoService);

  transform(
    item: NavigationBasicInfo | NavigationNearestItem | NavigationResourceBasicInfo,
    searchText: string
  ): string[] {
    const normalizedSearchText = this.normalizeText(searchText);
    const foundDataList = [];

    if (
      this.normalizeText(localized(item.displayNameForApp, this.translocoService)).indexOf(normalizedSearchText) > -1
    ) {
      foundDataList.push(localized(item.displayNameForApp, this.translocoService));
    } else if (this.normalizeText(localized(item.name, this.translocoService)).indexOf(normalizedSearchText) > -1) {
      foundDataList.push(localized(item.name, this.translocoService));
    }

    if (
      this.normalizeText(localized(item.resourceTypeName, this.translocoService)).indexOf(normalizedSearchText) > -1
    ) {
      foundDataList.push(localized(item.resourceTypeName, this.translocoService));
    }

    if ((item as unknown as any).eventData) {
      const matchEventDataListByOrganizer = (item as unknown as any).eventData.filter((x) =>
        this.normalizeText(x.organizer).includes(normalizedSearchText)
      );
      matchEventDataListByOrganizer.forEach((eventData) => {
        foundDataList.push(eventData.organizer);
      });
    }

    const matchQrCodeCategoryNameList = item.qrCodes.filter(
      (qr) => this.normalizeText(localized(qr.qrCategoryName, this.translocoService)).indexOf(normalizedSearchText) > -1
    );

    const matchQrCodeName = item.qrCodes.filter(
      (qr) => this.normalizeText(localized(qr.name, this.translocoService)).indexOf(normalizedSearchText) > -1
    );

    const matchQrCodeTagList = item.qrCodes.reduce<TagData[]>((tagList, qr) => {
      const matchingTagList = qr.tags.filter(
        (tag) => this.normalizeText(localized(tag.name, this.translocoService, true)).indexOf(normalizedSearchText) > -1
      );

      tagList.push(...matchingTagList);
      return tagList;
    }, []);

    matchQrCodeCategoryNameList.forEach((qr) => {
      foundDataList.push(localized(qr.qrCategoryName, this.translocoService));
    });

    matchQrCodeName.forEach((qr) => {
      foundDataList.push(localized(qr.name, this.translocoService));
    });

    matchQrCodeTagList.forEach((tag) => {
      foundDataList.push(localized(tag.name, this.translocoService, true));
    });

    const matchTagList = item.tags.filter(
      (qr) => this.normalizeText(localized(qr.name, this.translocoService)).indexOf(normalizedSearchText) > -1
    );

    matchTagList.forEach((qr) => {
      foundDataList.push(localized(qr.name, this.translocoService));
    });

    if (
      this.normalizeText(localized(item.resourceDescription, this.translocoService)).indexOf(normalizedSearchText) > -1
    ) {
      foundDataList.push(
        this.findTextWithContext(localized(item.resourceDescription, this.translocoService), normalizedSearchText)
      );
    }

    // if(item.resourceGuid === '92008265-235f-49c6-9ca2-9b0793e421c1'){
    //   debugger;
    // }

    const matchPropertyList = item.resourcePropertiesInfo?.filter(
      (prop) =>
        (localized(prop.valueText, this.translocoService, true) &&
          this.normalizeText(localized(prop.valueText, this.translocoService, true)).indexOf(normalizedSearchText) !==
            -1) ||
        (localized(prop.valueHTMLContent, this.translocoService, true) &&
          this.normalizeText(localized(prop.valueHTMLContent, this.translocoService, true)).indexOf(
            normalizedSearchText
          ) !== -1) ||
        (prop.valueDecimal && (prop.valueDecimal.toFixed(2) + '').indexOf(normalizedSearchText) !== -1) ||
        (prop.valueNumber && (prop.valueNumber + '').indexOf(normalizedSearchText) !== -1) ||
        (localized(prop.name, this.translocoService, true) &&
          this.normalizeText(localized(prop.name, this.translocoService, true)).indexOf(normalizedSearchText) !== -1)
    );

    matchPropertyList?.forEach((prop) => {
      const result =
        prop.valueDecimal ||
        prop.valueNumber ||
        localized(prop.valueText, this.translocoService, true) ||
        localized(prop.valueHTMLContent, this.translocoService, true);

      if (result) {
        foundDataList.push(`${localized(prop.name, this.translocoService, true)}: ${result}`);
      } else {
        foundDataList.push(localized(prop.name, this.translocoService, true));
      }
    });
    return foundDataList;
  }

  private normalizeText(text: string): string {
    if (text) {
      return remove(text.toUpperCase());
    }
    return '';
  }

  private findTextWithContext(description: string, searchText: string): string | null {
    if (!description || !searchText) return null;

    const normalizedWordList = this.normalizeText(description).split(/\s+/);
    const originWordList = description.split(/\s+/);

    // regex pro částečné hledání slova
    const searchRegex = new RegExp(`\\b(\\w*${searchText}\\w*)\\b`, 'i');

    for (let i = 0; i < normalizedWordList.length; i++) {
      const match = normalizedWordList[i].match(searchRegex);
      if (match) {
        const before = originWordList[i - 1] || '';
        const foundWord = originWordList[i]; // Nalezené slovo odpovídající regexu
        const after = originWordList[i + 1] || '';
        return `${before} ${foundWord} ${after}`.trim();
      }
    }

    return null;
  }
}
